import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled, useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Paper,
  Box,
  Tabs,
  Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Title from '../../components/Title';
import { Subscription } from '../../resources/types/balanceTypes';
import {
  getSubscriptionAsync,
  deleteSubscriptionAsync
} from '../../redux/slices/subscriptionSlice';
import { RootState } from '../../redux/store';
import SubscriptionModal from '../../modals/SubscriptionModal';
import { selectCities } from '../../redux/slices/citiesSlice';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import UserSubscriptionsTab from './components/UserSubscriptionsTab';
import SubscriptionPlansTab from './components/SubscriptionPlansTab';

const PREFIX = 'SubscriptionPage';

const classes = {
  header: `${PREFIX}-header`,
  addButton: `${PREFIX}-addButton`
};

const Root = styled('div')({
  [`& .${classes.header}`]: {
    display: 'flex',
    alignItems: 'center'
  },
  [`& .${classes.addButton}`]: {
    marginLeft: 20
  }
});

const SubscriptionPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const cities = useSelector(selectCities);
  const adminInfo = useSelector(selectAdmins);

  const cityId = useMemo(() => {
    const city = cities.items.find(item => item._id === adminInfo.currentCity);
    if (city) {
      return city._id;
    }
  }, [adminInfo.currentCity, cities.items]);

  const [open, setOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] =
    useState<Subscription | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    dispatch(getSubscriptionAsync(cityId ?? ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteSubscription = (_id?: string) => {
    if (!_id?.length) return;
    // eslint-disable-next-line no-restricted-globals
    const confirmation = confirm(
      t('subscriptions.confirmDelete')
    );
    if (confirmation) {
      dispatch(deleteSubscriptionAsync(_id, cityId ?? ''));
    }
  };

  return (
    <Root>
      <Paper sx={{ border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', borderRadius: 2, padding: '20px' }}>
        <div className={classes.header}>
          <Title>{t('subscriptions.title')}</Title>
          {activeTab === 0 && (
            <Button
              className={classes.addButton}
              color='primary'
              startIcon={<AddIcon />}
              variant='contained'
              onClick={() => {
                setOpen(true);
                setSelectedSubscription(null);
              }}
            >
              {t('subscriptions.createBtn')}
            </Button>
          )}
        </div>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3, mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                color: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
                '&.Mui-selected': {
                  color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF',
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.mode === 'light' ? '#424242' : '#5073F8',
                height: 3,
                borderRadius: '3px 3px 0 0'
              }
            }}
          >
            <Tab label={t('subscriptions.tabPlans')} />
            <Tab label={t('subscriptions.tabUserSubscriptions')} />
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <SubscriptionPlansTab
            onEdit={(subscription) => {
              setOpen(true);
              setSelectedSubscription(subscription);
            }}
            onDelete={deleteSubscription}
          />
        )}

        {activeTab === 1 && <UserSubscriptionsTab />}
      </Paper>
      <SubscriptionModal
        open={open}
        setOpen={setOpen}
        selectedSubscription={selectedSubscription}
        setSelectedSubscription={setSelectedSubscription}
      />
    </Root>
  );
};

export default SubscriptionPage;
